import React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    ArrowLeft,
    ArrowRight,
    Calendar,
    CheckCircle2,
    Clock3,
    Copy,
    CreditCard,
    Filter,
    Receipt,
    RefreshCcw,
    Search,
    Smartphone,
    TrendingDown,
    TrendingUp,
    Tv,
    Wallet,
    Wifi,
    X,
    XCircle,
    Zap,
    GraduationCap
} from 'lucide-react';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { getTransactionsHistory } from 'api/api.transaction';
import { TransactionReceiptModal } from 'ui-component/TransactionReceiptModal';
import moment from 'moment';

const PAGE_SIZE = 20;

const TRANSACTION_TYPE_OPTIONS = [
    { value: '', label: 'All', icon: <Filter size={16} /> },
    { value: 'WALLET_FUNDING', label: 'Wallet Funding', icon: <Wallet size={16} /> },
    { value: 'ADMIN_WALLET_FUNDING', label: 'Admin Wallet Funding', icon: <TrendingUp size={16} /> },
    { value: 'WALLET_DEDUCTING', label: 'Admin Wallet Deduct', icon: <TrendingDown size={16} /> },
    { value: 'DATA_ORDER', label: 'Data Order', icon: <Smartphone size={16} /> },
    { value: 'API_DATA_ORDER', label: 'API Data Order', icon: <Smartphone size={16} /> },
    { value: 'AIRTIME_ORDER', label: 'Airtime Order', icon: <Wifi size={16} /> },
    { value: 'API_AIRTIME_ORDER', label: 'API Airtime Order', icon: <Wifi size={16} /> },
    { value: 'ELECTRICITY_ORDER', label: 'Electricity Payment', icon: <Zap size={16} /> },
    { value: 'API_ELECTRICITY_ORDER', label: 'API Electricity Payment', icon: <Zap size={16} /> },
    { value: 'CABLE_ORDER', label: 'TV Payment', icon: <Tv size={16} /> },
    { value: 'API_CABLE_ORDER', label: 'API TV Payment', icon: <Tv size={16} /> },
    { value: 'RESULT_CHECK_ORDER', label: 'Result Check Payment', icon: <GraduationCap size={16} /> },
    { value: 'API_RESULT_CHECK_ORDER', label: 'API Result Check Payment', icon: <GraduationCap size={16} /> }
];

const formatAmount = (amount, currency = 'NGN') => {
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
    }).format(Number(amount || 0));
};

const formatNumber = (value) => {
    return new Intl.NumberFormat('en-NG').format(Number(value || 0));
};

const formatDate = (date) => {
    if (!date) return '--';
    try {
        return new Date(date).toLocaleString('en-NG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        return date;
    }
};

const prettifyText = (value) => {
    if (!value) return '--';
    return String(value)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusColor = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'success') return 'success';
    if (normalized === 'pending') return 'warning';
    if (normalized === 'failed' || normalized === 'error') return 'error';
    return 'default';
};

const getStatusConfig = (status) => {
    const normalized = String(status || '').toLowerCase();

    if (normalized === 'success') {
        return {
            color: 'success',
            icon: <CheckCircle2 size={16} />,
            label: 'Successful'
        };
    }

    if (normalized === 'pending') {
        return {
            color: 'warning',
            icon: <Clock3 size={16} />,
            label: 'Pending'
        };
    }

    return {
        color: 'error',
        icon: <XCircle size={16} />,
        label: 'Failed'
    };
};

const getTransactionIcon = (transaction = {}) => {
    const type = String(transaction?.transactionType || '').toUpperCase();

    if (type === 'DATA_ORDER' || type === 'API_DATA_ORDER') return <Smartphone size={18} />;
    if (type === 'AIRTIME_ORDER' || type === 'API_AIRTIME_ORDER') return <Wifi size={18} />;
    if (type === 'ELECTRICITY_ORDER' || type === 'API_ELECTRICITY_ORDER') return <Zap size={18} />;
    if (type === 'CABLE_ORDER' || type === 'API_CABLE_ORDER') return <Tv size={18} />;
    if (type === 'RESULT_CHECK_ORDER' || type === 'API_RESULT_CHECK_ORDER') return <GraduationCap size={18} />;
    if (type === 'WALLET_DEDUCTING') return <TrendingDown size={18} />;
    if (type === 'WALLET_FUNDING' || type === 'ADMIN_WALLET_FUNDING') return <TrendingUp size={18} />;

    if (type.includes('DEDUCT') || type.includes('DEBIT') || type.includes('ORDER')) {
        return <TrendingDown size={18} />;
    }

    if (type.includes('FUND') || type.includes('CREDIT')) {
        return <TrendingUp size={18} />;
    }

    return <Wallet size={18} />;
};

const getTransactionTypeLabel = (type) => {
    const found = TRANSACTION_TYPE_OPTIONS.find((item) => item.value === type);
    return found?.label || prettifyText(type);
};

const StatCard = ({ title, value, subtitle, icon }) => (
    <Box
        sx={{
            p: 2.25,
            borderRadius: '20px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
            minHeight: '118px'
        }}
    >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {title}
                </Typography>
                <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 800 }}>
                    {value}
                </Typography>
                {subtitle ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {subtitle}
                    </Typography>
                ) : null}
            </Box>

            <Box
                sx={{
                    width: 42,
                    height: 42,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '14px',
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                }}
            >
                {icon}
            </Box>
        </Stack>
    </Box>
);

const ReceiptRow = ({ label, value, bold = false }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ py: 1.1 }}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>

        <Typography
            variant="body2"
            sx={{
                fontWeight: bold ? 700 : 500,
                textAlign: 'right',
                maxWidth: '65%',
                wordBreak: 'break-word'
            }}
        >
            {value || '--'}
        </Typography>
    </Stack>
);

const TransactionItem = ({ transaction, onClick }) => {
    const theme = useTheme();

    return (
        <Box
            onClick={onClick}
            sx={{
                p: 2,
                borderRadius: '18px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
                    borderColor: alpha(theme.palette.primary.main, 0.35)
                }
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                    src={transaction?.imageUrl || undefined}
                    sx={{
                        width: 52,
                        height: 52,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: 'primary.main'
                    }}
                >
                    {!transaction?.imageUrl ? getTransactionIcon(transaction) : null}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                        direction={{ xs: 'row', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={1}
                        alignItems={{ xs: 'center', sm: 'center' }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                                {transaction?.name || getTransactionTypeLabel(transaction?.transactionType)}
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                <Chip
                                    label={prettifyText(transaction?.status)}
                                    size="small"
                                    color={getStatusColor(transaction?.status)}
                                    variant="filled"
                                />
                                {transaction?.service ? <Chip label={transaction.service} size="small" variant="outlined" /> : null}
                                <Chip label={getTransactionTypeLabel(transaction?.transactionType)} size="small" variant="outlined" />
                            </Stack>
                        </Box>

                        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                {formatAmount(transaction?.amount, transaction?.currency || 'NGN')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {moment(transaction?.createdAt).calendar()}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
};

const Transactions = () => {
    const [transactions, setTransactions] = React.useState([]);
    const [selected, setSelected] = React.useState(null);
    const [showModal, setShowModal] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [refreshing, setRefreshing] = React.useState(false);
    const [error, setError] = React.useState('');

    const [searchInput, setSearchInput] = React.useState('');
    const [searchQuery, setSearchQuery] = React.useState('');
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    const [transactionType, setTransactionType] = React.useState('');

    const [pagination, setPagination] = React.useState({
        totalCount: 0,
        currentPage: 1,
        prevPage: null,
        nextPage: null
    });

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchInput.trim());
        }, 500);

        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchTransactions = React.useCallback(
        async (page = 1, isRefresh = false) => {
            try {
                setError('');

                if (isRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const response = await getTransactionsHistory({
                    page,
                    limit: PAGE_SIZE,
                    searchQuery,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                    transactionType: transactionType || undefined
                });

                if (response?.ok) {
                    const payload = response?.data?.data || {};

                    setTransactions(payload.transactions || []);
                    setPagination({
                        totalCount: payload.totalCount || 0,
                        currentPage: payload.currentPage || 1,
                        prevPage: payload.prevPage ?? null,
                        nextPage: payload.nextPage ?? null
                    });
                } else {
                    setError(response?.data?.message || 'Unable to fetch transactions.');
                }
            } catch (err) {
                setError(err?.message || 'Something went wrong while fetching transactions.');
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [searchQuery, startDate, endDate, transactionType]
    );

    React.useEffect(() => {
        fetchTransactions(1);
    }, [fetchTransactions]);

    const totalAmount = React.useMemo(() => {
        return transactions.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    }, [transactions]);

    const successCount = React.useMemo(() => {
        return transactions.filter((item) => String(item.status || '').toLowerCase() === 'success').length;
    }, [transactions]);

    const emptyState = !loading && transactions.length === 0;

    const activeFiltersCount = [searchQuery, startDate, endDate, transactionType].filter(Boolean).length;

    const clearFilters = () => {
        setSearchInput('');
        setSearchQuery('');
        setStartDate('');
        setEndDate('');
        setTransactionType('');
    };

    return (
        <>
            <TransactionReceiptModal open={showModal} onClose={() => setShowModal(false)} selected={selected} />

            <MainCard>
                <Stack spacing={3}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={2}
                    >
                        <Box>
                            <Typography variant="h2" sx={{ fontWeight: 800 }}>
                                Transactions
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Track wallet funding, bill payments, deductions, and transaction history.
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1.5}>
                            <Button
                                variant="outlined"
                                startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshCcw size={16} />}
                                onClick={() => fetchTransactions(pagination.currentPage, true)}
                                disabled={loading || refreshing}
                            >
                                Refresh
                            </Button>
                        </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <StatCard
                                title="Total Transactions"
                                value={formatNumber(pagination.totalCount)}
                                subtitle={`Page ${pagination.currentPage}`}
                                icon={<Wallet size={20} />}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <StatCard
                                title="Successful On This Page"
                                value={formatNumber(successCount)}
                                subtitle="Completed transactions"
                                icon={<CheckCircle2 size={20} />}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <StatCard
                                title="Amount On This Page"
                                value={formatAmount(totalAmount)}
                                subtitle={`${transactions.length} record(s) loaded`}
                                icon={<CreditCard size={20} />}
                            />
                        </Box>
                    </Stack>

                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '20px',
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.default'
                        }}
                    >
                        <Stack spacing={2.2}>
                            <Stack
                                direction={{ xs: 'column', lg: 'row' }}
                                spacing={2}
                                justifyContent="space-between"
                                alignItems={{ xs: 'stretch', lg: 'center' }}
                            >
                                <TextField
                                    fullWidth
                                    placeholder="Search by name, reference, service, status..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search size={18} />
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Chip icon={<Calendar size={16} />} label={`Page ${pagination.currentPage}`} variant="outlined" />
                                    <Chip label={`${formatNumber(pagination.totalCount)} total`} variant="outlined" />
                                    {activeFiltersCount > 0 ? (
                                        <Chip
                                            color="primary"
                                            label={`${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''} active`}
                                        />
                                    ) : null}
                                </Stack>
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />

                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />

                                <FormControl fullWidth>
                                    <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
                                    <Select
                                        labelId="transaction-type-label"
                                        value={transactionType}
                                        label="Transaction Type"
                                        onChange={(e) => setTransactionType(e.target.value)}
                                    >
                                        {TRANSACTION_TYPE_OPTIONS.map((option) => (
                                            <MenuItem key={option.value || 'all'} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <Button
                                    variant="contained"
                                    startIcon={<Search size={16} />}
                                    onClick={() => fetchTransactions(1)}
                                    disabled={loading}
                                >
                                    Apply Filters
                                </Button>

                                <Button variant="outlined" startIcon={<X size={16} />} onClick={clearFilters} disabled={loading}>
                                    Clear Filters
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>

                    {error ? <Alert severity="error">{error}</Alert> : null}

                    {loading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                Loading transactions...
                            </Typography>
                        </Stack>
                    ) : emptyState ? (
                        <Box
                            sx={{
                                py: 8,
                                px: 3,
                                textAlign: 'center',
                                borderRadius: '20px',
                                border: '1px dashed',
                                borderColor: 'divider'
                            }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                No transactions found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Try adjusting your search, date range, or transaction type.
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={1.5}>
                            {transactions.map((transaction) => (
                                <TransactionItem
                                    key={transaction.id}
                                    transaction={transaction}
                                    onClick={() => {
                                        setSelected(transaction);
                                        setShowModal(true);
                                    }}
                                />
                            ))}
                        </Stack>
                    )}

                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '18px',
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper'
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Showing page <strong>{pagination.currentPage}</strong>
                            </Typography>

                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ArrowLeft size={16} />}
                                    disabled={!pagination.prevPage || loading}
                                    onClick={() => fetchTransactions(pagination.prevPage)}
                                >
                                    Previous
                                </Button>

                                <Button
                                    variant="contained"
                                    endIcon={<ArrowRight size={16} />}
                                    disabled={!pagination.nextPage || loading}
                                    onClick={() => fetchTransactions(pagination.nextPage)}
                                >
                                    Next
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </MainCard>
        </>
    );
};

export default Transactions;
